
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Answer, RecommendationPriority, RecommendedAction } from '@/types/assessment';
import { PriorityBadge } from './PriorityBadge';
import { ValueBadge } from './ValueBadge';
import { X, Plus } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface AnswerRecommendationsDrawerProps {
  answer: Answer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (recommendedActions: RecommendedAction[]) => void;
}

export function AnswerRecommendationsDrawer({
  answer,
  open,
  onOpenChange,
  onSave
}: AnswerRecommendationsDrawerProps) {
  const [recommendedActions, setRecommendedActions] = useState<RecommendedAction[]>(answer.recommendedActions || []);
  const [newAction, setNewAction] = useState('');
  const [newPriority, setNewPriority] = useState<RecommendationPriority>('medium');

  const handleAddAction = () => {
    if (newAction.trim()) {
      const newRecommendedAction: RecommendedAction = {
        id: crypto.randomUUID(),
        text: newAction.trim(),
        priority: newPriority
      };
      setRecommendedActions([...recommendedActions, newRecommendedAction]);
      setNewAction('');
      setNewPriority('medium');
    }
  };

  const handleRemoveAction = (id: string) => {
    setRecommendedActions(recommendedActions.filter(action => action.id !== id));
  };

  const handleSave = () => {
    onSave(recommendedActions);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md md:max-w-lg flex flex-col h-full">
        <div className="flex-1 overflow-y-auto pb-6 flex flex-col">
          <SheetHeader className="flex flex-col gap-1 relative">
            {/* Position the Value badge to the right with better spacing */}
            <ValueBadge value={answer.value} className="absolute right-8 top-0" />
            <SheetTitle>Recommended Actions</SheetTitle>
            <SheetDescription>
              Manage recommended actions for answer: {answer.text}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-6 space-y-6 flex-grow flex flex-col">
            {/* Add new action */}
            <div>
              <h4 className="text-sm font-medium mb-3">Add New Recommendation</h4>
              <div className="space-y-3 px-1"> {/* Added padding to prevent clipping */}
                <Input value={newAction} onChange={e => setNewAction(e.target.value)} placeholder="Enter recommendation text" />
                <div className="flex gap-2">
                  <Select value={newPriority} onValueChange={value => setNewPriority(value as RecommendationPriority)}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High Priority</SelectItem>
                      <SelectItem value="medium">Medium Priority</SelectItem>
                      <SelectItem value="low">Low Priority</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAddAction}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
            
            <Separator className="mx-1" /> {/* Added horizontal margin */}
            
            {/* Current recommendations list with flexible height */}
            <div className="flex-grow flex flex-col">
              <h4 className="text-sm font-medium mb-3">Current Recommendations</h4>
              <div className="space-y-2 overflow-y-auto pr-1 flex-grow min-h-[200px]">
                {recommendedActions.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    No recommendations added yet. Add some above.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-1">
                    {recommendedActions.map(action => (
                      <div key={action.id} className="p-3 border rounded-md bg-card shadow-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <PriorityBadge priority={action.priority} />
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveAction(action.id)} className="h-6 w-6 p-0">
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <p className="text-sm">{action.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer - Sticky at bottom */}
        <SheetFooter className="sticky bottom-0 pt-4 border-t mt-auto bg-background">
          <div className="flex justify-end w-full gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
